// sortaballs — puzzle loader. Reads sortaballs-10-colors-puzzles.json produced by the
// `10` generator script (solved very-hard states). The 905 KB JSON is NOT in
// the eager bundle: `?url` imports carry only the hashed file URL, the payload
// is fetched + parsed on first need, and a readiness subscription lets the
// game shell and sidebar re-render when it lands. getPuzzleById /
// getTotalPuzzleCount stay synchronous (undefined / 0 before load), and the
// stored-level store resyncs from storage once loaded.
// Base-specific delta: effort falls back to log10(statesExplored) when an entry
// ships no top-level effort. The generator writes the set pre-sorted by
// difficulty (easiest first); sort: true re-sorts at load as an idempotent
// invariant (ids follow the sorted order).

import e2ePuzzlesDataUrl from "../../e2e-puzzles/sortaballs-10-colors.json?url"
import puzzlesDataUrl from "../../sortaballs-10-colors-puzzles.json?url"
import { useSyncExternalStore } from "react"

import { E2E_SORTABALLS } from "../config/constants"
import { logger } from "../modules/logger/logger"
import { createPuzzleLoader } from "./puzzle-loader"

type Loader = ReturnType<typeof createPuzzleLoader>

let loader: null | Loader = null
let loading: null | Promise<void> = null
const listeners = new Set<()=> void>()

const notifyReady = (): void => {
  for (const listener of listeners) listener()
}

const subscribe = (listener: ()=> void): (()=> void) => {
  listeners.add(listener)
  return ()=> listeners.delete(listener)
}

const getReady = (): boolean => loader !== null

// True once the puzzle set is loaded. Static-loading variants never need this;
// the game shell and the sidebar panel consume it for the lazily-loaded set.
export const usePuzzlesReady = (): boolean =>
  useSyncExternalStore(subscribe, getReady, getReady)

// Fetches + parses the shipped set. The fetch also works in the production
// build (the ?url asset is emitted), and avoids dev's native import-attribute
// handling of dynamic JSON imports.
const fetchSource = async (): Promise<unknown> => {
  const response = await fetch(
    E2E_SORTABALLS ? e2ePuzzlesDataUrl : puzzlesDataUrl,
  )
  if (!response.ok) {
    throw new Error(`sortaballs puzzles fetch failed: ${response.status}`)
  }
  return response.json()
}

// Starts the load once (safe to call repeatedly; a failure resets so a later
// call retries). Fire-and-forget from the shell on mount so the fetch is
// already in flight when the user's first puzzle interaction begins.
export const warmPuzzles = (): Promise<void> => {
  if (loader) return Promise.resolve()
  if (!loading) {
    loading = fetchSource()
      .then((source)=> {
        loader = createPuzzleLoader({
          data: source,
          sort: true,
          resolveEffort: (entry)=> {
            if (entry.effort !== undefined) return entry.effort
            const explored = entry.game.statesExplored
            if (explored !== undefined && explored > 0) return Math.log10(explored)
            return undefined
          },
        })
        notifyReady()
      })
      .catch((error: unknown)=> {
        loading = null
        logger({ input: error as Error, level: "error" })
        throw error
      })
  }
  return loading
}

export const getPuzzleById = (id: number): ReturnType<Loader["getPuzzleById"]> =>
  loader?.getPuzzleById(id)

export const getTotalPuzzleCount = (): number => loader?.getTotalPuzzleCount() ?? 0